import { Loader2Icon, UserPlusIcon, UsersIcon } from "lucide-react"
import type * as React from "react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  USER_ROLES,
  USER_SIGN_IN_METHODS,
  type UserResponse,
  type UserRole,
  type UserSignInMethod,
} from "@steelbill/types"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { NativeSelect, NativeSelectOption } from "@/components/ui/native-select"
import { Skeleton } from "@/components/ui/skeleton"
import {
  learnDoors,
  useCreateUser,
  useSetUserCredential,
  useUpdateUser,
  useUsers,
  type KnownDoors,
} from "@/features/users/use-users"
import { ApiError } from "@/lib/api-error"
import { istDayOfInstant, useFormatDay } from "@/lib/dates"
import { istTimeOfInstant } from "@/features/messages/message-time"

/**
 * `/settings/users` — who has an account (spec §2, §13), behind `user.manage`.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THIS SCREEN IS WHAT MAKES THE CA'S LOGIN USABLE. The ACCOUNTANT role, her
 * capabilities and her sign-in door all shipped on 2026-08-17, and there was
 * still no way to bring her account into existence except a hand-written
 * `INSERT` against the production database — a bcrypt hash computed by hand on a
 * live tax system, and a role string with nothing to validate it. The API half
 * landed the same morning with nothing in front of it. This is the front.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * FOUR RULES IT EXISTS TO KEEP, AND EVERY ONE OF THEM DEFERS TO THE SERVER:
 *
 *  1. WHICH IDENTIFIER AND SECRET A ROLE TAKES IS NOT THIS SCREEN'S DECISION.
 *     `ROLE_CREDENTIAL_PATH` lives in the API and deliberately not in
 *     `@steelbill/types` — it is authentication, and an authentication rule
 *     compiled into the SPA is a rule somebody eventually enforces here, where
 *     it is decoration. So the form follows `signInWith`, which the server puts
 *     on every row, and asks in the one case where no row exists yet. Sending a
 *     PIN for an accountant is a 400 naming the field the server wanted.
 *
 *  2. THE REFUSALS ARE SHOWN VERBATIM AND NEVER PRE-EMPTED. Five of them —
 *     demoting yourself out of `user.manage` (403), switching your own account
 *     off (403), leaving nobody able to administer the system (409), moving an
 *     account to a door it cannot open (409), an identifier already in use (409)
 *     — each with a sentence naming what would have happened. A friendlier
 *     rewording would drop the part that says what to do instead, and a
 *     client-side copy of "would this leave nobody able to administer?" needs a
 *     count the browser does not hold.
 *
 *  3. `roleAvailable: false` IS SAID OUT LOUD, on the row and on the create.
 *     The CA's account can exist for days before `accountant_role` is switched
 *     on (§18.5) — the account and the session are different questions. Without
 *     the sentence this screen would show a perfectly ordinary active account
 *     and her first 403 would look like a bug in the software.
 *
 *  4. NOTHING IS DELETED AND NO DELETE CONTROL IS DRAWN. A user who has issued
 *     documents or authored audit rows must not vanish — the schema's `RESTRICT`
 *     and `SetNull` keys already say so, and there is no `DELETE /users/:id` to
 *     call. Deactivation is the operation, it is reversible, and it takes effect
 *     on the target's very next request (§14.2).
 *
 * A PASSWORD NEVER COMES BACK, so nothing here displays, echoes or stores one.
 * `hasCredential` is a boolean ABOUT a hash and is the only thing this screen
 * knows about a secret — which is exactly the fact an ADMIN needs when the
 * question is "why can she not get in?".
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NO FILTERS, AND THAT IS A DECISION. `GET /users` accepts `?role=` and
 * `?active=`, and this screen sends neither: the table holds three rows and they
 * are the same three rows every time. A control that can hide one of three is a
 * control that costs a tap to learn nothing, and it would make the create form
 * forget which door a filtered-out role uses (see `learnDoors`). The filters
 * stay on the hook for the day a fourth person exists.
 *
 * NO FEATURE FLAG EITHER, on the route or on the API. §18.3 forbids flagging
 * authentication, and a flag that could 404 this router could stop the last
 * administrator fixing a lockout — so a 404 here is a real 404 and `FeatureOff`
 * is the wrong panel for it.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function UsersScreen(): React.JSX.Element {
  const { t } = useTranslation()
  const users = useUsers()

  /*
   * WHAT THE SCREEN HAS LEARNED ABOUT EACH ROLE'S SIGN-IN DOOR, accumulated
   * across responses rather than read off the current page — see `learnDoors`,
   * which carries the argument for why the client is not allowed to simply know
   * this.
   */
  const [doors, setDoors] = useState<KnownDoors>({})
  useEffect(() => {
    const page = users.data?.users
    if (page === undefined) return
    setDoors((known) => learnDoors(known, page))
  }, [users.data])

  return (
    <div className="flex w-full flex-col gap-4 p-4 md:gap-6 md:p-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t("users.title")}</h1>
        <p className="text-muted-foreground text-sm">{t("users.screenHelp")}</p>
      </header>

      <AddUserCard doors={doors} />

      {users.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : users.isError ? (
        <Alert variant="destructive">
          <AlertTitle>{t("err.couldNotLoad")}</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>
              {users.error instanceof ApiError
                ? users.error.message
                : t("err.somethingWentWrong")}
            </span>
            <Button
              variant="outline"
              onClick={() => {
                void users.refetch()
              }}
            >
              {t("btn.retry")}
            </Button>
          </AlertDescription>
        </Alert>
      ) : users.data === undefined || users.data.users.length === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <UsersIcon />
            </EmptyMedia>
            <EmptyTitle>{t("users.emptyTitle")}</EmptyTitle>
            <EmptyDescription>{t("users.emptyHelp")}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <Card className="overflow-hidden py-0">
          <ul className="divide-border divide-y">
            {/* The server's order — by id, oldest first. Never re-sorted here. */}
            {users.data.users.map((user) => (
              <UserRow key={user.id} user={user} />
            ))}
          </ul>
        </Card>
      )}

      {/*
        * SAID OUT LOUD RATHER THAN LEFT AS AN ABSENCE. A reader who cannot find
        * a delete button assumes it is somewhere else on the screen; this
        * sentence says there is no such operation anywhere and names the one
        * that replaces it.
        */}
      <p className="text-muted-foreground text-sm">{t("users.neverDeleted")}</p>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Making an account                                                           */
/* -------------------------------------------------------------------------- */

function AddUserCard({ doors }: { doors: KnownDoors }): React.JSX.Element {
  const { t } = useTranslation()
  const create = useCreateUser()

  const [role, setRole] = useState<UserRole>("ACCOUNTANT")
  const [displayName, setDisplayName] = useState("")
  const [identifier, setIdentifier] = useState("")
  const [secret, setSecret] = useState("")
  /*
   * THE FALLBACK WHEN NO ACCOUNT OF THIS ROLE EXISTS YET — which is exactly the
   * CA on the day this screen is needed. It defaults to the PASSWORD door, and
   * the reason is not "most roles use it": the PIN door is 10^6 possibilities
   * and exists for one man on a workshop floor, so an unknown role must not be
   * nudged towards the weaker credential. The control is visible and changeable,
   * and the server refuses the wrong answer with a sentence naming the field it
   * wanted — this is a default, never a rule.
   */
  const [chosenDoor, setChosenDoor] = useState<UserSignInMethod>("password")

  const knownDoor = doors[role]
  const door: UserSignInMethod = knownDoor ?? chosenDoor

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    /*
     * EXACTLY THE PAIR THIS DOOR TAKES, AND NOTHING ELSE. `UserCreateSchema` is
     * `.strict()` and the service refuses a body naming the other door's fields,
     * so sending both "to be safe" is a 400 rather than a kindness.
     */
    const body: Record<string, unknown> =
      door === "pin"
        ? { role, displayName, phone: identifier, pin: secret }
        : { role, displayName, email: identifier, password: secret }

    create.mutate(body, {
      onSuccess: () => {
        // The secret is dropped the moment it has been sent. It is never read
        // back, never re-shown and never kept for a "confirm" step.
        setDisplayName("")
        setIdentifier("")
        setSecret("")
      },
    })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("users.addTitle")}</CardTitle>
        <CardDescription>{t("users.addIntro")}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="newUserName">{t("users.displayName")}</Label>
              <Input
                id="newUserName"
                value={displayName}
                onChange={(event) => {
                  setDisplayName(event.target.value)
                }}
              />
              <p className="text-muted-foreground text-xs">{t("users.displayNameHelp")}</p>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="newUserRole">{t("users.role")}</Label>
              {/*
                * NATIVE, like the state picker on Settings: this is an ADMIN
                * screen at a desk, the list is three entries long, and the
                * value it carries is the capability set the account will hold.
                */}
              <NativeSelect
                id="newUserRole"
                className="w-full"
                value={role}
                onChange={(event) => {
                  setRole(event.target.value as UserRole)
                }}
              >
                {USER_ROLES.map((option) => (
                  <NativeSelectOption key={option} value={option}>
                    {t(`role.${option.toLowerCase()}`)}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </div>
          </div>

          {/*
            * THE DOOR. Read off `signInWith` when any account of this role
            * exists, asked for when none does — never derived from the role
            * name, which would be `ROLE_CREDENTIAL_PATH` reimplemented in a
            * component.
            */}
          {knownDoor === undefined ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="newUserDoor">{t("users.door")}</Label>
              <NativeSelect
                id="newUserDoor"
                className="w-full"
                value={chosenDoor}
                onChange={(event) => {
                  setChosenDoor(event.target.value as UserSignInMethod)
                }}
              >
                {USER_SIGN_IN_METHODS.map((method) => (
                  <NativeSelectOption key={method} value={method}>
                    {t(method === "pin" ? "users.doorPin" : "users.doorPassword")}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
              <p className="text-muted-foreground text-xs">
                {t("users.doorUnknownHelp", { role: t(`role.${role.toLowerCase()}`) })}
              </p>
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">
              {t("users.doorKnown", {
                role: t(`role.${role.toLowerCase()}`),
                door: t(knownDoor === "pin" ? "users.doorPin" : "users.doorPassword"),
              })}
            </p>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="newUserIdentifier">
                {t(door === "pin" ? "users.phone" : "users.email")}
              </Label>
              <Input
                id="newUserIdentifier"
                inputMode={door === "pin" ? "tel" : "email"}
                value={identifier}
                onChange={(event) => {
                  setIdentifier(event.target.value)
                }}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="newUserSecret">
                {t(door === "pin" ? "users.pin" : "users.password")}
              </Label>
              {/*
                * `type="password"` on both: a 6-digit PIN typed on a shared
                * office desktop is as worth covering as a password is. It is
                * never read back — the value leaves in the request body and the
                * field is cleared.
                */}
              <Input
                id="newUserSecret"
                type="password"
                autoComplete="new-password"
                inputMode={door === "pin" ? "numeric" : "text"}
                value={secret}
                onChange={(event) => {
                  setSecret(event.target.value)
                }}
              />
              <p className="text-muted-foreground text-xs">
                {t(door === "pin" ? "users.pinHelp" : "users.passwordHelp")}
              </p>
            </div>
          </div>

          <ServerReply
            error={create.error}
            warnings={create.data?.warnings}
            success={create.isSuccess ? t("users.created") : null}
          />

          <div className="flex justify-end">
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? <Loader2Icon className="animate-spin" /> : <UserPlusIcon />}
              {t("users.addSubmit")}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

/* -------------------------------------------------------------------------- */
/* One account                                                                 */
/* -------------------------------------------------------------------------- */

function UserRow({ user }: { user: UserResponse }): React.JSX.Element {
  const { t } = useTranslation()
  const formatDay = useFormatDay()
  const [open, setOpen] = useState(false)

  /*
   * ONE MUTATION PAIR PER ROW, so a refusal on one account cannot paint an error
   * under another. The hooks are called inside this component for exactly that
   * reason.
   */
  const update = useUpdateUser()
  const credential = useSetUserCredential()

  const [displayName, setDisplayName] = useState(user.displayName)
  const [role, setRole] = useState<UserRole>(user.role)
  const [secret, setSecret] = useState("")

  const doorWord = t(user.signInWith === "pin" ? "users.doorWordPin" : "users.doorWordPassword")
  const roleLabel = t(`role.${user.role.toLowerCase()}`)

  /*
   * A LOCKOUT IS AN INSTANT, NOT A DAY. `istDayOfInstant` first and the IST
   * clock time beside it — slicing the ISO string to ten characters would print
   * the UTC day, and everything between 00:00 and 05:30 IST would report
   * yesterday. A 15-minute lockout with yesterday's date on it is worse than no
   * date at all.
   */
  const lockedUntil =
    user.lockedUntil === null
      ? null
      : t("users.whenAt", {
          day: formatDay(istDayOfInstant(user.lockedUntil)),
          time: istTimeOfInstant(user.lockedUntil),
        })

  const saveDetails = () => {
    /*
     * ALL THREE FIELDS, EVERY TIME, and the server decides what actually moved —
     * it writes one audit row per dimension that changed and answers "Nothing
     * changed" as a warning when none did. Sending only the dirty fields would
     * mean this screen deciding what a change is, which is the same job done
     * twice and only one of them audited.
     */
    update.mutate({ id: user.id, body: { displayName, role, isActive: user.isActive } })
  }

  const setActive = (isActive: boolean) => {
    /*
     * NO CLIENT-SIDE GUARD, DELIBERATELY. Switching off your own account is a
     * 403 and switching off the last administrator is a 409, each with a
     * sentence naming what would have happened and what to do instead. Hiding
     * the button would replace that sentence with silence, and the two cases
     * turn on a count of other active administrators that the browser does not
     * hold.
     */
    update.mutate({ id: user.id, body: { isActive } })
  }

  const setCredential = (event: React.FormEvent) => {
    event.preventDefault()
    credential.mutate(
      {
        id: user.id,
        // Which key the server wants is decided by the TARGET's role, on the
        // server. `signInWith` is that decision, already made.
        body: user.signInWith === "pin" ? { pin: secret } : { password: secret },
      },
      {
        onSuccess: () => {
          setSecret("")
        },
      },
    )
  }

  return (
    <li className="flex flex-col gap-3 p-4" data-testid="user-row">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold">{user.displayName}</span>
        {/*
          * THE ACCOUNT NUMBER, BECAUSE THE SERVER'S SENTENCES USE IT. Every
          * refusal from this router names the target as `#2` — "Changing #2 from
          * ADMIN to OWNER would leave nobody able to administer SteelBill" — and
          * those sentences are shown verbatim rather than reworded. Without the
          * id on the row the reader has to work out which of three accounts a
          * refusal is about.
          */}
        <span className="text-muted-foreground text-xs">
          {t("users.accountNumber", { id: user.id })}
        </span>
        <Badge variant="outline">{roleLabel}</Badge>
        {user.isActive ? (
          <Badge variant="secondary">{t("users.on")}</Badge>
        ) : (
          <Badge variant="destructive">{t("users.off")}</Badge>
        )}
      </div>

      <div className="flex flex-col gap-0.5">
        <p className="text-muted-foreground text-sm">
          {user.phone ?? user.email ?? t("users.noIdentifier")}
        </p>
        <p className="text-muted-foreground text-sm">
          {t(user.signInWith === "pin" ? "users.signsInWithPin" : "users.signsInWithPassword")}
        </p>
      </div>

      {/*
        * WHY THIS PERSON CANNOT GET IN — the three answers, each stated rather
        * than left for the reader to infer from a blank. An ADMIN asking the
        * question should not have to guess, and the payload carries every one of
        * these fields specifically so it can be answered.
        */}
      {user.hasCredential ? null : (
        <p className="text-destructive text-sm">{t("users.noCredential", { door: doorWord })}</p>
      )}

      {user.roleAvailable ? null : (
        <Alert>
          {/*
            * NO `role` ON THE TITLE. "Cannot sign in yet" carries no
            * placeholder in either locale, so the value was being handed to
            * i18next and thrown away in silence — the same shape as the
            * `counters.nowInvoice` defect of 2026-08-17, caught here by the
            * placeholder guard in `__tests__/i18n-completeness.test.ts`. The
            * role is already on screen twice: the badge above and the sentence
            * below, which does interpolate it.
            */}
          <AlertTitle>{t("users.roleUnavailableTitle")}</AlertTitle>
          <AlertDescription>{t("users.roleUnavailableHelp", { role: roleLabel })}</AlertDescription>
        </Alert>
      )}

      {lockedUntil === null ? null : (
        <p className="text-destructive text-sm">{t("users.lockedUntil", { when: lockedUntil })}</p>
      )}

      {user.failedLoginCount > 0 ? (
        <p className="text-muted-foreground text-sm">
          {t("users.failedLogins", { count: user.failedLoginCount })}
        </p>
      ) : null}

      <div>
        <Button
          variant="outline"
          size="sm"
          aria-expanded={open}
          onClick={() => {
            setOpen((was) => !was)
          }}
        >
          {open ? t("users.hideManage") : t("users.manage", { name: user.displayName })}
        </Button>
      </div>

      {open ? (
        <div className="flex flex-col gap-4 border-t pt-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`name-${user.id}`}>{t("users.displayName")}</Label>
              <Input
                id={`name-${user.id}`}
                value={displayName}
                onChange={(event) => {
                  setDisplayName(event.target.value)
                }}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`role-${user.id}`}>{t("users.role")}</Label>
              <NativeSelect
                id={`role-${user.id}`}
                className="w-full"
                value={role}
                onChange={(event) => {
                  setRole(event.target.value as UserRole)
                }}
              >
                {USER_ROLES.map((option) => (
                  <NativeSelectOption key={option} value={option}>
                    {t(`role.${option.toLowerCase()}`)}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
              <p className="text-muted-foreground text-xs">{t("users.roleChangeHelp")}</p>
            </div>
          </div>

          <div className="flex flex-wrap justify-end gap-2">
            <Button
              variant="outline"
              disabled={update.isPending}
              onClick={() => {
                setActive(!user.isActive)
              }}
            >
              {user.isActive ? t("users.switchOff") : t("users.switchOn")}
            </Button>
            <Button disabled={update.isPending} onClick={saveDetails}>
              {update.isPending ? <Loader2Icon className="animate-spin" /> : null}
              {t("btn.saveChanges")}
            </Button>
          </div>

          <ServerReply
            error={update.error}
            warnings={update.data?.warnings}
            success={update.isSuccess ? t("users.saved") : null}
          />

          <form onSubmit={setCredential} className="flex flex-col gap-1.5 border-t pt-4">
            <Label htmlFor={`credential-${user.id}`}>
              {t("users.credentialTitle", { door: doorWord })}
            </Label>
            <Input
              id={`credential-${user.id}`}
              type="password"
              autoComplete="new-password"
              inputMode={user.signInWith === "pin" ? "numeric" : "text"}
              value={secret}
              onChange={(event) => {
                setSecret(event.target.value)
              }}
            />
            {/*
              * THE ONLY UNLOCK THIS API HAS, said where the control is. Setting
              * a credential clears `failed_login_count` and `locked_until`
              * (§11A); there is deliberately no "unlock but keep the password"
              * route, because an account freezes precisely when somebody has
              * been guessing at it. An ADMIN who does not know that goes looking
              * for a button that was never built.
              */}
            <p className="text-muted-foreground text-xs">{t("users.credentialHelp")}</p>
            <div className="flex justify-end">
              <Button type="submit" variant="outline" disabled={credential.isPending}>
                {credential.isPending ? <Loader2Icon className="animate-spin" /> : null}
                {t("users.credentialSubmit", { door: doorWord })}
              </Button>
            </div>
            <ServerReply
              error={credential.error}
              warnings={credential.data?.warnings}
              success={credential.isSuccess ? t("users.credentialSet", { door: doorWord }) : null}
            />
          </form>
        </div>
      ) : null}
    </li>
  )
}

/* -------------------------------------------------------------------------- */
/* What the server said                                                        */
/* -------------------------------------------------------------------------- */

/**
 * The server's own words, unedited — a refusal, a warning, or a confirmation.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * IT RENDERS `error.message` VERBATIM AND TRANSLATES NOTHING. Every refusal this
 * router produces is a sentence written for the person reading this screen, and
 * each one names what would have happened and what to do instead — "that removes
 * your user.manage permission … ask another administrator", "no other active
 * account holds user.manage … there is no way back in from the app once the last
 * one is gone". A friendlier local rewording throws away the second half, which
 * is the half that prevents somebody editing the database by hand.
 *
 * The one substitution is a 5xx, where `lib/api.ts` has already discarded the
 * server's message: there is nothing to show and nothing the reader can act on.
 * `warnings` gets the same treatment — never an error, always worth showing.
 * ═════════════════════════════════════════════════════════════════════════════
 */
function ServerReply({
  error,
  warnings,
  success,
}: {
  error: unknown
  warnings: string[] | undefined
  success: string | null
}): React.JSX.Element | null {
  const { t } = useTranslation()

  if (error !== null && error !== undefined) {
    const apiError = error instanceof ApiError ? error : null

    return (
      <Alert variant="destructive">
        <AlertTitle>{t("err.couldNotSave")}</AlertTitle>
        <AlertDescription className="flex flex-col gap-1">
          <span>
            {apiError === null || apiError.isServerError
              ? t("err.somethingWentWrong")
              : apiError.message}
          </span>
          {/* A 400's per-field detail, which names the field the schema refused. */}
          {apiError?.details.map((detail) => (
            <span key={`${detail.field}:${detail.message}`}>
              {detail.field}: {detail.message}
            </span>
          ))}
        </AlertDescription>
      </Alert>
    )
  }

  if (warnings !== undefined && warnings.length > 0) {
    return (
      <Alert>
        <AlertTitle>{success ?? t("status.saved")}</AlertTitle>
        <AlertDescription className="flex flex-col gap-1">
          {warnings.map((warning) => (
            <span key={warning}>{warning}</span>
          ))}
        </AlertDescription>
      </Alert>
    )
  }

  if (success === null) return null
  return <p className="text-muted-foreground text-sm">{success}</p>
}
